import { useMediaQuery } from '@mnd-frontend/hooks';
import Stepper from '@mnd-frontend/ui/public/components/Stepper';
import { Icons } from '@mnd-frontend/ui/publish/components';
import { breakpoint } from '@mnd-frontend/ui/shared/style-mixins/breakpoints';
import { SIZING_5, SPACING_2, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import throttle from 'lodash-es/throttle';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BlogCardGridModuleMediaQuery, type CardIndexUnion } from './BlogCardGridModule';

const DIRECTION = {
  PREV: 'prev',
  NEXT: 'next',
} as const;

const IndicatorWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${SPACING_2};
    margin-top: ${SPACING_4};

    ${breakpoint.md`
        display: none;
    `}
`;

const IconButton = styled.button`
    background-color: transparent;
    width: ${SIZING_5};
    height: ${SIZING_5};
    color: #FF4473; // token: red/reg
    border-radius: 50%;

    &:disabled {
        color: #ACACBB; // token: grey/reg
    }
`;

const BlogCardsIndicator = ({
  scrollContainerRef,
  targetCardRef,
  informSelectedCardIndex,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
  targetCardRef: RefObject<HTMLDivElement>;
  informSelectedCardIndex: Dispatch<SetStateAction<CardIndexUnion>>;
}) => {
  const { t } = useTranslation();
  const isLargeDevice = useMediaQuery(BlogCardGridModuleMediaQuery);
  const [indicatorNum, setIndicatorNum] = useState(0);

  const calcScrollDistance = useCallback(
    (direction: (typeof DIRECTION)[keyof typeof DIRECTION]) => {
      if (isLargeDevice) return 0;

      const containerWidth = scrollContainerRef?.current?.clientWidth ?? 0;

      // mid card
      if (indicatorNum === 1) {
        return direction === DIRECTION.PREV ? 0 : containerWidth * 2;
      }
      // last card
      if (indicatorNum === 2) {
        return direction === DIRECTION.PREV ? containerWidth : containerWidth * 2;
      }

      return containerWidth;
    },
    [scrollContainerRef?.current, indicatorNum, isLargeDevice],
  );

  const indicatorAction = useMemo(() => {
    const midCardPos = targetCardRef?.current?.getClientRects()[0];

    return {
      prev: {
        disabled: indicatorNum === 0,
        onClick: () => {
          scrollContainerRef?.current?.scroll({
            top: midCardPos?.y,
            left: calcScrollDistance(DIRECTION.PREV),
            behavior: 'smooth',
          });
        },
      },
      next: {
        disabled: indicatorNum === 2,
        onClick() {
          scrollContainerRef?.current?.scroll({
            top: midCardPos?.y,
            left: calcScrollDistance(DIRECTION.NEXT),
            behavior: 'smooth',
          });
        },
      },
    };
  }, [indicatorNum, scrollContainerRef?.current, targetCardRef?.current, calcScrollDistance]);

  useEffect(() => {
    const scrollEvent = throttle(() => {
      const cardPosition = targetCardRef.current?.getClientRects()[0];

      if (cardPosition) {
        if (cardPosition.x < 0) {
          setIndicatorNum(2);
          informSelectedCardIndex(2);
        } else if (cardPosition.x > cardPosition.width) {
          setIndicatorNum(0);
          informSelectedCardIndex(0);
        } else {
          setIndicatorNum(1);
          informSelectedCardIndex(1);
        }
      }
    }, 800);

    if (scrollContainerRef?.current) {
      scrollContainerRef.current.addEventListener('scroll', scrollEvent);
    }

    return () => {
      scrollContainerRef.current?.removeEventListener('scroll', scrollEvent);
    };
  }, [targetCardRef.current, scrollContainerRef.current, informSelectedCardIndex]);

  if (isLargeDevice) return null;

  return (
    <IndicatorWrapper>
      <IconButton
        disabled={indicatorAction.prev.disabled}
        onClick={indicatorAction.prev.onClick}
        aria-label={t('common_previous')}
      >
        <Icons.ArrowLeft aria-hidden />
      </IconButton>
      <Stepper numOfSteps={3} activeStepIndex={indicatorNum} />
      <IconButton
        disabled={indicatorAction.next.disabled}
        onClick={indicatorAction.next.onClick}
        aria-label={t('common_next')}
      >
        <Icons.ArrowRight aria-hidden />
      </IconButton>
    </IndicatorWrapper>
  );
};

export default BlogCardsIndicator;
